import { useEffect, useLayoutEffect } from "react";
import { Routes, Route, useLocation, useSearchParams } from "react-router-dom";

import "./App.scss";
import { designerPagePath, initAnalytics, trackPage } from "./utilities/analytics";
import {
  LIBRARY_COLUMNS_EVENT,
  readLibraryColumns,
} from "./utilities/libraryColumns";
import {
  blockPageZoomGesture,
  blockPageZoomKey,
  blockPageZoomWheel,
} from "./utilities/blockPageZoom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MaterialContextProvider from "./context/MaterialContext";
import UserContextProvider from "./context/UserContext";
import ProtectedRoutes from "./utilities/ProtectedRoutes";
import VendorRoutes from "./utilities/VendorRoutes";
import AdminRoutes from "./utilities/AdminRoutes";
import RegisterRoutes from "./utilities/RegisterRoutes";
import Footer from "./components/Footer";

import Landing from "./pages/Landing";
import LoginForm from "./components/LoginForm";
import Library from "./pages/Library";
import Collection from "./pages/Collection";
import CollectionPdf from "./pages/CollectionPdf";
import Contact from "./pages/Contact";
import SingleMaterial from "./pages/SingleMaterial";
import MaterialAdd from "./pages/vendor/MaterialAdd";
import MaterialEdit from "./pages/vendor/MaterialEdit";
import MaterialPrice from "./pages/vendor/MaterialPrice";
import VendorSettings from "./pages/vendor/VendorSettings";
import UnprotectedRoutes from "./utilities/UnprotectedRoutes";
import Help from "./pages/Help";
import ExternalEntry from "./pages/ExternalEntry";
import ExternalRoutes from "./utilities/ExternalRoutes";
import RegisterAccount from "./pages/RegisterAccount";
import ChangePassword from "./pages/ChangePassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnmount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  const location = useLocation();
  //const params = useParams();
  const [queryParameters] = useSearchParams();

  useLayoutEffect(() => {
    const applyLibraryColumns = () => {
      document.documentElement.dataset.libraryColumns = String(
        readLibraryColumns(),
      );
    };

    applyLibraryColumns();
    window.addEventListener(LIBRARY_COLUMNS_EVENT, applyLibraryColumns);
    return () => {
      window.removeEventListener(LIBRARY_COLUMNS_EVENT, applyLibraryColumns);
    };
  }, []);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const desktopQuery = "(hover: hover) and (pointer: fine)";
    const desktop =
      typeof window.matchMedia === "function" &&
      window.matchMedia(desktopQuery).matches;
    if (!desktop) {
      return undefined;
    }

    const listenerOptions = { passive: false, capture: true };
    window.addEventListener("wheel", blockPageZoomWheel, listenerOptions);
    window.addEventListener("keydown", blockPageZoomKey, listenerOptions);
    document.addEventListener(
      "gesturestart",
      blockPageZoomGesture,
      listenerOptions,
    );
    document.addEventListener(
      "gesturechange",
      blockPageZoomGesture,
      listenerOptions,
    );

    return () => {
      window.removeEventListener("wheel", blockPageZoomWheel, listenerOptions);
      window.removeEventListener("keydown", blockPageZoomKey, listenerOptions);
      document.removeEventListener(
        "gesturestart",
        blockPageZoomGesture,
        listenerOptions,
      );
      document.removeEventListener(
        "gesturechange",
        blockPageZoomGesture,
        listenerOptions,
      );
    };
  }, []);

  useEffect(() => {
    trackPage(designerPagePath(location.pathname, location.search));
  }, [location.pathname, location.search]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <UserContextProvider>
          <MaterialContextProvider>
            <Routes>
              <Route path="/help" element={<Help />}></Route>
              {/* EXPOSED ROUTES */}

              <Route element={<UnprotectedRoutes />}>
                <Route path="/contact" element={<Contact />}></Route>
              </Route>
              <Route element={<ExternalRoutes />}>
                <Route
                  path="/ext"
                  element={
                    queryParameters.size === 0 ? <Landing /> : <ExternalEntry />
                  }
                ></Route>
              </Route>
              <Route path="/" element={<Landing />}>
                <Route index element={<LoginForm brand={location.pathname} />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password" element={<ResetPassword />} />
              </Route>

              {/* PROTECTED ROUTES */}
              <Route element={<ProtectedRoutes />}>
                {/* DESIGNER AND GUEST VIEWABLE */}
                <Route path="/library" element={<Library />} />
                <Route
                  path="/loading"
                  element={<div>Getting your PDF ready</div>}
                />
                <Route path="/collection" element={<Collection />} />
                <Route path="/collection/pdf" element={<CollectionPdf />} />
                <Route path="/account/password" element={<ChangePassword />} />

                <Route element={<RegisterRoutes />}>
                  <Route path="/register" element={<RegisterAccount />} />
                </Route>

                <Route element={<AdminRoutes />}>
                  <Route path="/settings" element={<VendorSettings />} />
                </Route>

                <Route path="/:vendor/:slug" element={<SingleMaterial />} />

                {/* VENDOR ONLY */}
                <Route element={<VendorRoutes />}>
                  <Route path="/material/price" element={<MaterialPrice />} />
                  <Route path="/material/add" element={<MaterialAdd />} />
                  <Route
                    path="material/edit/:vendor/:slug"
                    element={<MaterialEdit />}
                  />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>

            <Footer path={location.pathname} />
          </MaterialContextProvider>
        </UserContextProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
